import React, { useMemo, useCallback } from 'react';
import logger from '../utils/logger';

interface BoundingBox {
  text: string;
  page_number: number;
  page_width: number;
  page_height: number;
  unit: string;
  bounding_box: [number, number, number, number, number, number, number, number];
  confidence: number;
}

interface DocumentHighlighterProps {
  ocrResult: any | null;
  highlightedValues: Array<{
    value: string;
    fieldName: string;
    color: string;
    // NEW: Direct bounding box data from extracted_data
    boundingBox?: {
      bounding_box: number[]; // [x1,y1, x2,y2, x3,y3, x4,y4]
      page_number: number;
      confidence?: number;
    };
  }>;
  viewportWidth: number;
  viewportHeight: number;
  pageNumber?: number;
  zoomLevel?: number;
}

const DocumentHighlighter: React.FC<DocumentHighlighterProps> = ({
  ocrResult,
  highlightedValues,
  viewportWidth,
  viewportHeight,
  pageNumber = 1,
  zoomLevel = 100,
}) => {
  // Find text locations in OCR results
  const findTextLocations = useCallback(
    (searchText: string): BoundingBox[] => {
      if (!ocrResult || !ocrResult.pages) {
        return [];
      }

      const locations: BoundingBox[] = [];
      const searchLower = searchText.toString().toLowerCase().trim();

      for (const page of ocrResult.pages) {
        // Only highlight for current page
        if (page.page_number !== pageNumber) continue;

        for (const word of page.words || []) {
          if (word.text.toLowerCase() === searchLower) {
            locations.push({
              text: word.text,
              page_number: page.page_number,
              page_width: page.width,
              page_height: page.height,
              unit: page.unit,
              bounding_box: word.bounding_box,
              confidence: word.confidence,
            });
          }
        }
      }

      return locations;
    },
    [ocrResult, pageNumber]
  );

  // Convert OCR coordinates to viewport coordinates
  const convertToViewportCoords = useCallback(
    (location: BoundingBox) => {
      // Bounding box is [x1, y1, x2, y2, x3, y3, x4, y4] representing 4 corners
      // Top-left, Top-right, Bottom-right, Bottom-left
      const [x1, y1, x2, y2, x3, y3, x4, y4] = location.bounding_box;

      // Find min/max to get bounding rectangle
      const minX = Math.min(x1, x2, x3, x4);
      const maxX = Math.max(x1, x2, x3, x4);
      const minY = Math.min(y1, y2, y3, y4);
      const maxY = Math.max(y1, y2, y3, y4);

      // With react-pdf, viewport dimensions ARE the actual PDF render dimensions!
      // No need for aspect ratio calculations - we get exact pixel dimensions from Page.onLoadSuccess

      // Calculate scale factors: viewport pixels per OCR inch
      const scaleX = viewportWidth / location.page_width;
      const scaleY = viewportHeight / location.page_height;

      // Apply zoom
      const zoom = zoomLevel / 100;

      const coords = {
        left: minX * scaleX * zoom,
        top: minY * scaleY * zoom,
        width: (maxX - minX) * scaleX * zoom,
        height: (maxY - minY) * scaleY * zoom,
      };

      return coords;
    },
    [viewportWidth, viewportHeight, zoomLevel]
  );

  // Generate all highlights
  const highlights = useMemo(() => {
    const result: JSX.Element[] = [];

    highlightedValues.forEach((item, itemIndex) => {
      let locations: BoundingBox[] = [];

      // NEW: Check if we have direct bounding box data
      if (item.boundingBox && item.boundingBox.bounding_box) {
        // Only highlight if it's on the current page
        if (item.boundingBox.page_number === pageNumber) {
          // Get page dimensions from OCR result
          const page = ocrResult?.pages?.find((p: any) => p.page_number === pageNumber);

          if (page) {
            locations = [
              {
                text: item.value.toString(),
                page_number: item.boundingBox.page_number,
                page_width: page.width,
                page_height: page.height,
                unit: page.unit || 'inch',
                bounding_box: item.boundingBox.bounding_box as [
                  number,
                  number,
                  number,
                  number,
                  number,
                  number,
                  number,
                  number,
                ],
                confidence: item.boundingBox.confidence || 1.0,
              },
            ];
          } else {
            logger.warn(
              '[DocumentHighlighter] Could not find page data for direct bbox'
            );
          }
        }
      } else {
        // FALLBACK: Use text search for backward compatibility
        locations = findTextLocations(item.value);
      }

      locations.forEach((location, locIndex) => {
        const coords = convertToViewportCoords(location);

        result.push(
          <div
            key={`${itemIndex}-${locIndex}`}
            className="highlight-overlay absolute pointer-events-auto"
            style={{
              left: `${coords.left}px`,
              top: `${coords.top}px`,
              width: `${coords.width}px`,
              height: `${coords.height}px`,
              minWidth: '20px', // Ensure visible minimum size
              minHeight: '20px',
              backgroundColor: `${item.color}66`, // 40% opacity (increased from 20%)
              border: `3px solid ${item.color}`, // Thicker border
              borderRadius: '4px',
              transition: 'all 0.2s ease',
              zIndex: 1000,
              boxShadow: `0 0 12px ${item.color}80, 0 2px 8px rgba(0,0,0,0.3)`, // Added color glow
              outline: `1px solid rgba(255,255,255,0.5)`, // White outline for contrast
            }}
            title={`${item.fieldName}: ${item.value}\nConfidence: ${(location.confidence * 100).toFixed(1)}%`}
          ></div>
        );
      });
    });

    return result;
  }, [
    highlightedValues,
    findTextLocations,
    convertToViewportCoords,
    viewportWidth,
    viewportHeight,
    zoomLevel,
    pageNumber,
    ocrResult,
  ]);

  if (!ocrResult || highlightedValues.length === 0) {
    return null;
  }

  return (
    <div
      className="document-highlighter-container absolute top-0 left-0 pointer-events-none"
      style={{
        width: '100%',
        height: '100%',
        zIndex: 999,
      }}
    >
      {highlights}
    </div>
  );
};

export default DocumentHighlighter;
